import React from 'react';
import './WaitingRoom.css';

interface WaitingRoomProps {
  gameType: 'classic-ludo' | 'naija-ludo' | 'chess' | 'checkers';
  gameId: string;
  playersConnected: number;
  maxPlayers: number;
  isLoading: boolean;
  onStartGame: () => void;
  onCancel?: () => void;
  playerNames?: string[];
}

const WaitingRoom: React.FC<WaitingRoomProps> = ({
  gameType,
  gameId,
  playersConnected,
  maxPlayers,
  isLoading,
  onStartGame,
  onCancel,
  playerNames = [],
}) => {
  const gameConfig = {
    'classic-ludo': {
      icon: '🎲',
      title: 'Classic Ludo',
      description: 'The timeless board game with traditional rules',
      color: '#4F46E5',
      gradient: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
    },
    'naija-ludo': {
      icon: '🎯',
      title: 'Naija Ludo',
      description: 'Nigerian style Ludo with unique gameplay mechanics',
      color: '#059669',
      gradient: 'linear-gradient(135deg, #059669, #10B981)',
    },
    chess: {
      icon: '♟️',
      title: 'Chess',
      description: 'The ultimate strategy game for tactical minds',
      color: '#7C2D12',
      gradient: 'linear-gradient(135deg, #7C2D12, #A16207)',
    },
    checkers: {
      icon: '⚫',
      title: 'Checkers',
      description: 'Classic strategy with simple rules, deep gameplay',
      color: '#DC2626',
      gradient: 'linear-gradient(135deg, #DC2626, #EA580C)',
    },
  };

  const config = gameConfig[gameType];

  const copyGameId = () => {
    navigator.clipboard.writeText(gameId);
    // TODO: Add toast notification
  };

  const shareGameLink = () => {
    const gameUrl = `${window.location.origin}?join=${gameId}`;
    if (navigator.share) {
      navigator.share({
        title: `Join my ${config.title} game!`,
        text: `Let's play ${config.title} together!`,
        url: gameUrl,
      });
    } else {
      navigator.clipboard.writeText(gameUrl);
      // TODO: Add toast notification
    }
  };

  return (
    <div className="waiting-room">
      <div className="waiting-room-container">
        {/* Header */}
        <div
          className="waiting-room-header"
          style={{ background: config.gradient }}
        >
          <div className="game-icon">{config.icon}</div>
          <h1 className="game-title">{config.title}</h1>
          <p className="game-description">{config.description}</p>
        </div>

        {/* Top Banner Ad Placeholder */}
        <div className="ad-banner-top">
          <div className="ad-placeholder">
            <span className="ad-label">📢 Sponsored</span>
            <div className="ad-content">
              <p>
                🎮 <strong>Play more games!</strong> Discover Chess, Checkers &
                more
              </p>
              <small>Perfect time to explore while waiting</small>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="waiting-room-content">
          {/* Status Section */}
          <div className="status-section">
            <div className="status-icon">
              <div className="pulse-animation">⏳</div>
            </div>
            <h2 className="status-title">Game Room Created!</h2>
            <p className="status-subtitle">
              Waiting for players to join your {config.title.toLowerCase()} game
            </p>
          </div>

          {/* Game Info */}
          <div className="game-info-section">
            <div className="info-card">
              <div className="info-label">Game ID</div>
              <div className="info-value">
                <code className="game-id-code">{gameId}</code>
                <button
                  onClick={copyGameId}
                  className="copy-button"
                  title="Copy Game ID"
                >
                  📋
                </button>
              </div>
            </div>

            <div className="info-card">
              <div className="info-label">Players</div>
              <div className="info-value">
                <span className="player-count">
                  {playersConnected}/{maxPlayers}
                </span>
                <div className="player-dots">
                  {Array.from({ length: maxPlayers }, (_, i) => (
                    <div
                      key={i}
                      className={`player-dot ${i < playersConnected ? 'active' : 'inactive'}`}
                      style={
                        {
                          '--player-color': config.color,
                        } as React.CSSProperties
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Mid-Content Ad Placeholder */}
          <div className="ad-section-middle">
            <div className="ad-placeholder-compact">
              <span className="ad-label">💰 Sponsored</span>
              <div className="ad-content-compact">
                <span className="ad-text">
                  🔥 <strong>Pi Network Gaming Rewards!</strong> Earn while you
                  play
                </span>
                <button className="ad-cta">Learn More</button>
              </div>
            </div>
          </div>

          {/* Player List */}
          {playerNames.length > 0 && (
            <div className="players-section">
              <h3 className="players-title">Players in Game</h3>
              <div className="players-list">
                {playerNames.map((name, index) => (
                  <div key={index} className="player-item">
                    <div className="player-avatar">
                      {name.charAt(0).toUpperCase()}
                    </div>
                    <span className="player-name">{name}</span>
                    {index === 0 && <span className="host-badge">Host</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-section">
            <button
              onClick={onStartGame}
              className="start-game-button"
              style={{ background: config.gradient }}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="loading-spinner"></div>
                  Starting Game...
                </>
              ) : (
                <>
                  <span className="button-icon">🚀</span>
                  Start Game
                  {playersConnected < maxPlayers && ' (Demo Mode)'}
                </>
              )}
            </button>

            <div className="secondary-actions">
              <button onClick={shareGameLink} className="share-button">
                <span className="button-icon">📤</span>
                Invite Friends
              </button>

              {onCancel && (
                <button onClick={onCancel} className="cancel-button">
                  <span className="button-icon">❌</span>
                  Cancel Game
                </button>
              )}
            </div>
          </div>

          {/* Security Badge */}
          <div className="security-section">
            <div className="security-badge">
              <span className="security-icon">🔐</span>
              <div className="security-text">
                <span className="security-title">Cryptographically Secure</span>
                <span className="security-subtitle">
                  Server-side game engine with anti-cheat protection
                </span>
              </div>
            </div>
          </div>

          {/* Pre-Tips Ad Placeholder */}
          <div className="ad-section-bottom">
            <div className="ad-placeholder-square">
              <span className="ad-label">🎯 Featured</span>
              <div className="ad-content-square">
                <div className="ad-visual">🎪</div>
                <div className="ad-details">
                  <h4>Join Gaming Tournaments!</h4>
                  <p>Compete with players worldwide</p>
                  <button className="ad-cta-primary">Join Now</button>
                </div>
              </div>
            </div>
          </div>

          {/* Tips Section */}
          <div className="tips-section">
            <h4 className="tips-title">💡 Quick Tips</h4>
            <ul className="tips-list">
              <li>Share the Game ID with friends to invite them</li>
              <li>You can start with fewer players in demo mode</li>
              <li>All dice rolls are cryptographically secure</li>
              <li>Game state is automatically saved and synchronized</li>
            </ul>
          </div>

          {/* Footer Ad Placeholder */}
          <div className="ad-footer">
            <div className="ad-placeholder-footer">
              <span className="ad-label">🎮 Gaming Network</span>
              <div className="ad-content-footer">
                <span>Discover more Pi Network games & apps</span>
                <button className="ad-cta-small">Explore</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitingRoom;
